import { useMemo } from 'react'
import { VWAM_SIGNUP_URL } from '@/lib/links'
import {
  AnalyticsContext,
  AuthContext,
  BasenameContext,
  DocsBaseUrlContext,
  FetcherContext,
  SignupUrlContext,
  SurfaceContext,
} from './context'
import type {
  TrackerAnalytics,
  TrackerProviderProps,
  TrackerSurface,
} from './TrackerProvider.types'

const noopAnalytics = { track: () => {} }

// Wrap the shell's analytics so EVERY event carries `surface` without each call
// site passing it. Per-call props win on collision (a deliberate override beats
// the default). When there's no analytics impl, stay a no-op.
function withSurface(
  analytics: TrackerAnalytics | undefined,
  surface: TrackerSurface
): TrackerAnalytics {
  if (!analytics) return noopAnalytics

  return {
    track: (event, props) => analytics.track(event, { surface, ...props }),
  }
}

// Normalize a basename: '' (root) or '/prefix' with no trailing slash.
function normalizeBasename(basename: string | undefined): string {
  if (!basename || basename === '/') return ''
  const withLeading = basename.startsWith('/') ? basename : `/${basename}`

  return withLeading.endsWith('/') ? withLeading.slice(0, -1) : withLeading
}

/**
 * The single provider both shells wrap their tree in (spec §5.1).
 * Injects the opaque transport (`fetcher`), identity (`auth`), an optional
 * analytics impl, and the mount-point `basename`. The package talks to the
 * outside world ONLY through these.
 * NOTE: this is NOT a router — each shell provides <BrowserRouter> (free) or
 * already has one (embedded). See <TrackerApp> for the route definitions.
 */
export function TrackerProvider({
  fetcher,
  auth,
  analytics,
  surface = 'free',
  basename,
  signupUrl,
  docsBaseUrl,
  children,
}: TrackerProviderProps) {
  const surfacedAnalytics = useMemo(
    () => withSurface(analytics, surface),
    [analytics, surface]
  )

  return (
    <FetcherContext.Provider value={fetcher}>
      <AuthContext.Provider value={auth}>
        <AnalyticsContext.Provider value={surfacedAnalytics}>
          <BasenameContext.Provider value={normalizeBasename(basename)}>
            <SurfaceContext.Provider value={surface}>
              <DocsBaseUrlContext.Provider value={docsBaseUrl ?? ''}>
                <SignupUrlContext.Provider value={signupUrl || VWAM_SIGNUP_URL}>
                  {children}
                </SignupUrlContext.Provider>
              </DocsBaseUrlContext.Provider>
            </SurfaceContext.Provider>
          </BasenameContext.Provider>
        </AnalyticsContext.Provider>
      </AuthContext.Provider>
    </FetcherContext.Provider>
  )
}
